import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { useWealthboxClientLink } from '../../../../../api/wealthbox'
import { QUERY_KEYS } from '../../../../../api/queryKeys'
import { scopeRequiresClientLink } from '../shared/wealthboxQueryHelpers'
import presets from './presets'

// Mirrors useRedtailMetricPreset, but Wealthbox has no family-member fan-out -
// scope === 'client' resolves to a single contactId via useWealthboxClientLink;
// scope === 'user' translates `assigned_to` server-side from the resolved token;
// scope === 'all' applies no automatic binding (firm-wide composition).
export default function useWealthboxMetricPreset (presetKey, config = {}) {
  const { clientId, userId } = useAppContext()
  const scope = config.scope || 'client'
  const needsLink = scopeRequiresClientLink(scope)
  const link = useWealthboxClientLink(clientId, { enabled: needsLink })

  const preset = presets[presetKey]
  const contactId = link.data?.externalId ?? null

  const metricQuery = useQuery({
    queryKey: [QUERY_KEYS.wealthboxDomainQuery, 'metric-preset', userId, clientId, presetKey, config.scope, config.createdByScope, config.updatedWithinDays, config.staleAfterDays, config.includeCompleted, config.taskType, config.teamId, contactId],
    queryFn: async () => {
      return preset.fetchData({
        scope,
        contactId,
        clientId,
        fullList: false
      }, config)
    },
    enabled: needsLink
      ? !!preset && link.data?.linked === true && !!contactId
      : !!preset
  })

  const value = metricQuery.data ?? null

  const merged = useMemo(() => {
    if (!preset) return { title: 'Unknown Preset', label: '', icon: null, format: '0', iconColor: null, valueColor: null }
    return {
      title: config.hideTitle ? null : (config.title ?? preset.defaults.title),
      label: config.hideLabel ? null : (config.label ?? preset.defaults.label),
      icon: config.hideIcon ? null : (config.icon ?? preset.defaults.icon),
      format: config.format ?? preset.defaults.format,
      iconColor: config.iconColor ?? null,
      valueColor: config.valueColor ?? null
    }
  }, [preset, config.title, config.hideTitle, config.label, config.hideLabel, config.icon, config.hideIcon, config.format, config.iconColor, config.valueColor])

  return {
    value,
    contactId,
    DetailPanel: preset?.DetailPanel,
    isLoading: needsLink
      ? link.isLoading || metricQuery.isFetching
      : metricQuery.isFetching,
    notLinked: needsLink ? link.data?.linked === false : false,
    error: needsLink
      ? link.error || metricQuery.error
      : metricQuery.error,
    ...merged
  }
}
